import React, { useState, useEffect } from 'react';
import { Table, Modal, message, Input, Select } from 'antd';
import { api, getAdminErrorMessage } from '../lib/api';
import { showBulkActionFeedback } from '../lib/bulkActionFeedback';
import BulkActionModal from '../components/BulkActionModal';
const USER_MANAGEMENT_VIEW_KEY = 'admin_user_management_view';

const pillStyle = (backgroundColor, color) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '4px 10px',
  backgroundColor,
  color,
  fontSize: '12px',
  fontWeight: 600,
  borderRadius: '999px'
});

const buttonStyle = (variant = 'neutral', disabled = false) => {
  const variants = {
    neutral: {
      background: '#f3f6fb',
      color: '#1f2a44',
      boxShadow: 'inset 0 0 0 1px rgba(148, 163, 184, 0.22)'
    },
    primary: {
      background: 'linear-gradient(135deg, #1677ff 0%, #2457d6 100%)',
      color: '#ffffff',
      boxShadow: '0 10px 20px rgba(22, 119, 255, 0.22)'
    },
    success: {
      background: 'linear-gradient(135deg, #34c759 0%, #159947 100%)',
      color: '#ffffff',
      boxShadow: '0 10px 20px rgba(21, 153, 71, 0.2)'
    },
    danger: {
      background: 'linear-gradient(135deg, #ff5f6d 0%, #d9363e 100%)',
      color: '#ffffff',
      boxShadow: '0 10px 20px rgba(217, 54, 62, 0.2)'
    }
  };
  const style = variants[variant] || variants.neutral;
  return {
    padding: '7px 14px',
    background: style.background,
    color: style.color,
    border: 'none',
    borderRadius: '999px',
    boxShadow: disabled ? 'none' : style.boxShadow,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    opacity: disabled ? 0.7 : 1
  };
};

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [bulkStatusModal, setBulkStatusModal] = useState({ open: false, status: null });
  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(USER_MANAGEMENT_VIEW_KEY) || 'null');
      if (!saved) return;
      setKeyword(saved.keyword || '');
      setRoleFilter(saved.roleFilter || 'all');
      setStatusFilter(saved.statusFilter || 'all');
      setPageSize(saved.pageSize || 10);
      setSortBy(saved.sortBy || 'createdAt');
      setSortDirection(saved.sortDirection || 'desc');
    } catch (_) {
      localStorage.removeItem(USER_MANAGEMENT_VIEW_KEY);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchUsers();
    }, 250);
    return () => clearTimeout(timer);
  }, [keyword, roleFilter, statusFilter, page, pageSize, sortBy, sortDirection]);

  useEffect(() => {
    localStorage.setItem(USER_MANAGEMENT_VIEW_KEY, JSON.stringify({
      keyword,
      roleFilter,
      statusFilter,
      pageSize,
      sortBy,
      sortDirection
    }));
  }, [keyword, roleFilter, statusFilter, pageSize, sortBy, sortDirection]);

  useEffect(() => {
    setPage(1);
  }, [keyword, roleFilter, statusFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/admin/users', {
        params: {
          keyword: keyword.trim() || undefined,
          role: roleFilter === 'all' ? undefined : roleFilter,
          status: statusFilter === 'all' ? undefined : statusFilter,
          page,
          pageSize,
          sortBy,
          sortDirection
        }
      });
      setUsers(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      message.error(getAdminErrorMessage(error, '加载失败'));
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    try {
      await api.post(`/api/admin/users/${userId}/toggle-status`, {
        status: currentStatus === 'active' ? 'banned' : 'active'
      });
      message.success('操作成功');
      fetchUsers();
    } catch (error) {
      message.error(error.response?.data?.message || '操作失败');
    }
  };

  const openDetail = (record) => {
    setSelectedUser(record);
    setEditingUser({
      nickname: record.nickname || '',
      phone: record.phone || '',
      email: record.email || '',
      role: record.role || 'USER',
      status: record.status || 'active'
    });
    setDetailVisible(true);
  };

  const handleSaveUser = async () => {
    if (!selectedUser || !editingUser) return;
    setSaving(true);
    try {
      const response = await api.put(`/api/admin/users/${selectedUser.id}`, editingUser);
      message.success('保存成功');
      setSelectedUser(response.data);
      setEditingUser({
        nickname: response.data.nickname || '',
        phone: response.data.phone || '',
        email: response.data.email || '',
        role: response.data.role || 'USER',
        status: response.data.status || 'active'
      });
      fetchUsers();
    } catch (error) {
      message.error(error.response?.data?.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const openBulkStatusModal = (status) => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择用户');
      return;
    }
    setBulkStatusModal({ open: true, status });
  };

  const submitBulkStatus = async (ids) => {
    const normalizedIds = ids || selectedRowKeys;
    if (!bulkStatusModal.status || normalizedIds.length === 0) return;
    setBulkSubmitting(true);
    try {
      const response = await api.post('/api/admin/users/bulk-status', {
        ids: normalizedIds,
        status: bulkStatusModal.status
      });
      showBulkActionFeedback(response.data, {
        entityLabel: '用户',
        successVerb: '处理',
        onSelectFailed: response.data.failedIds?.length ? (failedIds) => setSelectedRowKeys(failedIds) : undefined,
        onRetry: response.data.failedIds?.length ? submitBulkStatus : undefined
      });
      if (!response.data.failedIds?.length) {
        setSelectedRowKeys([]);
      }
      setBulkStatusModal({ open: false, status: null });
      fetchUsers();
    } catch (error) {
      message.error(error.response?.data?.message || '批量更新失败');
    } finally {
      setBulkSubmitting(false);
    }
  };

  const handleBulkStatus = async () => {
    await submitBulkStatus();
  };

  const resetFilters = () => {
    setKeyword('');
    setRoleFilter('all');
    setStatusFilter('all');
    setSortBy('createdAt');
    setSortDirection('desc');
    setPage(1);
    setPageSize(10);
    localStorage.removeItem(USER_MANAGEMENT_VIEW_KEY);
  };

  const sortLabel = getUserSortLabel(sortBy, sortDirection);

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 100,
      ellipsis: true
    },
    {
      title: '头像',
      dataIndex: 'avatarUrl',
      key: 'avatarUrl',
      width: 80,
      render: (avatarUrl, record) => (
        avatarUrl ? (
          <img
            src={avatarUrl}
            alt={record.nickname || record.username}
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              objectFit: 'cover'
            }}
          />
        ) : (
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              backgroundColor: '#E6F4FF',
              color: '#1677FF',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '14px',
              fontWeight: 600
            }}
          >
            {(record.nickname || record.username || '?').slice(0, 1).toUpperCase()}
          </div>
        )
      )
    },
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
      width: 120,
      sorter: true,
      sortOrder: sortBy === 'username' ? toAntdSortOrder(sortDirection) : null
    },
    {
      title: '昵称',
      dataIndex: 'nickname',
      key: 'nickname',
      width: 120,
      sorter: true,
      sortOrder: sortBy === 'nickname' ? toAntdSortOrder(sortDirection) : null
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      key: 'phone',
      width: 130
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      width: 100,
      sorter: true,
      sortOrder: sortBy === 'role' ? toAntdSortOrder(sortDirection) : null,
      render: (role) => (
        <span style={pillStyle(role === 'ADMIN' ? '#FFF3E0' : '#F3F4F6', role === 'ADMIN' ? '#C77700' : '#4B5563')}>
          {role === 'ADMIN' ? '管理员' : '普通用户'}
        </span>
      )
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 90,
      sorter: true,
      sortOrder: sortBy === 'status' ? toAntdSortOrder(sortDirection) : null,
      render: (status) => (
        <span style={pillStyle(status === 'active' ? '#EAFBF1' : '#FFF1F2', status === 'active' ? '#157347' : '#BE123C')}>
          {status === 'active' ? '正常' : '已封禁'}
        </span>
      )
    },
    {
      title: '注册时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      sorter: true,
      sortOrder: sortBy === 'createdAt' ? toAntdSortOrder(sortDirection) : null
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => {
              openDetail(record);
            }}
            style={buttonStyle('neutral')}
          >
            查看
          </button>
          <button
            onClick={() => handleToggleStatus(record.id, record.status)}
            style={buttonStyle(record.status === 'active' ? 'danger' : 'success')}
          >
            {record.status === 'active' ? '封禁' : '解封'}
          </button>
        </div>
      )
    }
  ];

  return (
    <div style={{ padding: '8px', background: 'transparent', minHeight: '100%' }}>
      <div style={{ marginBottom: '20px' }}>
        <div style={{ fontSize: '28px', fontWeight: 700, color: '#1f2a44', marginBottom: '6px' }}>
          用户管理
        </div>
        <div style={{ fontSize: '14px', color: '#6b7280' }}>
          查看账号状态、基础资料与权限信息，支持直接调整运营字段。
        </div>
      </div>

      <div style={toolbarStyle}>
        <Input
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder="搜索用户名、昵称、手机号、邮箱"
          allowClear
          style={filterInputStyle}
        />
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} style={filterSelectStyle}>
          <option value="all">全部角色</option>
          <option value="ADMIN">管理员</option>
          <option value="USER">普通用户</option>
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={filterSelectStyle}>
          <option value="all">全部状态</option>
          <option value="active">正常</option>
          <option value="banned">已封禁</option>
        </select>
        <button onClick={() => openBulkStatusModal('active')} style={buttonStyle('success')}>批量解封</button>
        <button onClick={() => openBulkStatusModal('banned')} style={buttonStyle('danger')}>批量封禁</button>
        <button onClick={resetFilters} style={buttonStyle('neutral')}>恢复默认</button>
        <div style={toolbarHintStyle}>
          {selectedRowKeys.length > 0 ? `已选 ${selectedRowKeys.length} 个 · ` : ''}
          当前排序：{sortLabel} · 共 {total} 条
        </div>
      </div>

      <div style={{ 
        backgroundColor: 'rgba(255, 255, 255, 0.92)',
        borderRadius: '18px',
        boxShadow: '0 20px 45px rgba(31, 42, 68, 0.08)',
        overflow: 'hidden'
      }}>
        <Table
          columns={columns}
          dataSource={users}
          loading={loading}
          rowKey="id"
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys
          }}
          onChange={(pagination, _, sorter) => {
            const nextSorter = Array.isArray(sorter) ? sorter[0] : sorter;
            setPage(pagination.current || 1);
            setPageSize(pagination.pageSize || 10);
            if (nextSorter?.field) {
              setSortBy(String(nextSorter.field));
              setSortDirection(nextSorter.order === 'ascend' ? 'asc' : 'desc');
            } else {
              setSortBy('createdAt');
              setSortDirection('desc');
            }
          }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (nextPage, nextPageSize) => {
              setPage(nextPage);
              setPageSize(nextPageSize);
            }
          }}
          scroll={{ x: 1120 }}
          size="middle"
          bordered={false}
        />
      </div>

      <Modal
        title="用户详情"
        open={detailVisible}
        onCancel={() => setDetailVisible(false)}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <button
              onClick={() => setDetailVisible(false)}
              style={buttonStyle('neutral')}
            >
              关闭
            </button>
            <button
              onClick={handleSaveUser}
              disabled={saving}
              style={buttonStyle('primary', saving)}
            >
              {saving ? '保存中...' : '保存'}
            </button>
          </div>
        }
        width={600}
      >
        {selectedUser && editingUser && (
          <div style={formGridStyle}>
            <ReadonlyField label="用户ID" value={selectedUser.id} />
            <ReadonlyField label="用户名" value={selectedUser.username} />
            <EditableField label="昵称">
              <Input
                value={editingUser.nickname}
                onChange={(e) => setEditingUser({ ...editingUser, nickname: e.target.value })}
                style={inputStyle}
              />
            </EditableField>
            <EditableField label="手机号">
              <Input
                value={editingUser.phone}
                onChange={(e) => setEditingUser({ ...editingUser, phone: e.target.value })}
                placeholder="未设置"
                style={inputStyle}
              />
            </EditableField>
            <EditableField label="邮箱">
              <Input
                value={editingUser.email}
                onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                placeholder="未设置"
                style={inputStyle}
              />
            </EditableField>
            <EditableField label="角色">
              <Select
                value={editingUser.role}
                onChange={(value) => setEditingUser({ ...editingUser, role: value })}
                style={{ width: '100%' }}
                options={[
                  { value: 'USER', label: '普通用户' },
                  { value: 'ADMIN', label: '管理员' }
                ]}
              />
            </EditableField>
            <EditableField label="状态">
              <Select
                value={editingUser.status}
                onChange={(value) => setEditingUser({ ...editingUser, status: value })}
                style={{ width: '100%' }}
                options={[
                  { value: 'active', label: '正常' },
                  { value: 'banned', label: '已封禁' }
                ]}
              />
            </EditableField>
            <ReadonlyField label="注册时间" value={selectedUser.createdAt} />
            <ReadonlyField label="最后登录" value={selectedUser.lastLoginAt || '未登录'} />
          </div>
        )}
      </Modal>

      <BulkActionModal
        open={bulkStatusModal.open}
        title={bulkStatusModal.status === 'active' ? '批量解封用户' : '批量封禁用户'}
        confirmText={bulkStatusModal.status === 'active' ? '确认批量解封' : '确认批量封禁'}
        confirmVariant={bulkStatusModal.status === 'active' ? 'success' : 'danger'}
        confirmLoading={bulkSubmitting}
        onCancel={() => setBulkStatusModal({ open: false, status: null })}
        onConfirm={handleBulkStatus}
        summaryLines={[
          `选中用户：${selectedRowKeys.length} 个`,
          `处理动作：${bulkStatusModal.status === 'active' ? '批量解封' : '批量封禁'}`
        ]}
        note={bulkStatusModal.status === 'active' ? '批量解封后这些账号会立即恢复登录与业务操作能力。' : '批量封禁会立即影响用户登录和业务使用，请确认这一批账号都需要限制。'}
      />
    </div>
  );
};

const fieldLabelStyle = {
  fontSize: '12px',
  color: '#7b8794',
  marginBottom: '6px',
  fontWeight: 600
};

const formGridStyle = {
  display: 'grid',
  gap: '12px'
};

const toolbarStyle = {
  display: 'flex',
  gap: '12px',
  flexWrap: 'wrap',
  alignItems: 'center',
  padding: '16px 18px',
  marginBottom: '18px',
  backgroundColor: 'rgba(255, 255, 255, 0.82)',
  borderRadius: '16px',
  boxShadow: '0 14px 30px rgba(31, 42, 68, 0.06)'
};

const filterInputStyle = {
  flex: '1 1 280px',
  minWidth: '220px'
};

const filterSelectStyle = {
  minWidth: '160px',
  height: '40px',
  padding: '0 12px',
  borderRadius: '12px',
  border: '1px solid #d6e4ff',
  backgroundColor: '#f8fbff',
  color: '#1f2a44'
};

const toolbarHintStyle = {
  marginLeft: 'auto',
  color: '#6b7280',
  fontSize: '13px',
  fontWeight: 600
};

const toAntdSortOrder = (direction) => direction === 'asc' ? 'ascend' : 'descend';

const getUserSortLabel = (sortBy, sortDirection) => {
  const labels = {
    createdAt: '注册时间',
    username: '用户名',
    nickname: '昵称',
    role: '角色',
    status: '状态'
  };
  return `${labels[sortBy] || '注册时间'}${sortDirection === 'asc' ? '升序' : '降序'}`;
};

const inputStyle = {
  height: '42px',
  border: '1px solid #d6e4ff',
  borderRadius: '12px',
  backgroundColor: '#f8fbff'
};

const fieldCardStyle = {
  padding: '12px 16px',
  backgroundColor: '#f8fbff',
  borderRadius: '14px',
  border: '1px solid #e6eefc',
  color: '#1f2a44',
  lineHeight: 1.7,
  wordBreak: 'break-word'
};

const ReadonlyField = ({ label, value }) => (
  <div>
    <div style={fieldLabelStyle}>{label}</div>
    <div style={fieldCardStyle}>{value}</div>
  </div>
);

const EditableField = ({ label, children }) => (
  <div>
    <div style={fieldLabelStyle}>{label}</div>
    {children}
  </div>
);

export default UserManagement;
